import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { MessageCircle, Inbox, FileText, Briefcase, Wrench, TrendingUp, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-white/10 bg-white/[0.02] p-4 sm:p-5 flex items-center gap-3 sm:gap-4 min-w-0" data-testid={testid}>
    <span className="text-nx-goldlight shrink-0"><Icon size={20} /></span>
    <div className="min-w-0">
      <p className="text-xl sm:text-2xl font-semibold text-white leading-none">{value}</p>
      <p className="text-[10px] tracking-[0.14em] sm:tracking-[0.18em] uppercase text-slate-500 mt-1.5 break-words">{label}</p>
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-nx-navy2 border border-white/15 px-4 py-3 text-xs">
      <p className="text-slate-400 mb-1.5">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }} className="capitalize">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export const AdminAnalytics = ({ onAuthFail }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/overview");
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else setError(true);
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-slate-500 text-sm" data-testid="analytics-error">Could not load analytics.</p>;
  if (!data) return <p className="text-slate-500 text-sm">Loading…</p>;

  const { days, totals, today, visitors } = data;
  const busiest = days.reduce((best, d) => (d.chats + d.enquiries > (best?.chats + best?.enquiries || 0) ? d : best), null);

  return (
    <div className="flex flex-col gap-8" data-testid="admin-analytics">
      {visitors && (
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4 flex items-center gap-2"><Eye size={13} className="text-nx-goldlight" /> Website visitors (unique)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={Eye} label="Today" value={visitors.today.visitors} testid="analytics-visitors-today" />
            <StatCard icon={Eye} label="Last 7 days" value={visitors.week.visitors} testid="analytics-visitors-week" />
            <StatCard icon={Eye} label="Last 30 days" value={visitors.month.visitors} testid="analytics-visitors-month" />
          </div>
          <p className="text-[11px] text-slate-600 mt-3">{visitors.today.pageviews} page views today · {visitors.week.pageviews} this week · {visitors.month.pageviews} this month — counted anonymously, no personal data stored.</p>
        </div>
      )}
      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4 flex items-center gap-2"><TrendingUp size={13} className="text-nx-goldlight" /> Today (IST)</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={MessageCircle} label="Chats today" value={today.chats} testid="analytics-today-chats" />
          <StatCard icon={Inbox} label="Enquiries today" value={today.enquiries} testid="analytics-today-enquiries" />
          <StatCard icon={MessageCircle} label="Total chats" value={totals.chats} testid="analytics-total-chats" />
          <StatCard icon={Inbox} label="Total enquiries" value={totals.enquiries} testid="analytics-total-enquiries" />
        </div>
      </div>

      <div className="border border-white/10 bg-white/[0.02] p-5 sm:p-6" data-testid="analytics-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Last {days.length} days — chats vs enquiries</p>
          {busiest && busiest.chats + busiest.enquiries > 0 && (
            <p className="text-xs text-nx-goldlight" data-testid="analytics-busiest">Busiest: {busiest.label} ({busiest.chats + busiest.enquiries} total)</p>
          )}
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days} margin={{ top: 4, right: 8, left: -18, bottom: 0 }} barGap={3}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#94a3b8" }} />
              <Bar dataKey="chats" name="Chats" fill="#d4af5e" radius={[2, 2, 0, 0]} maxBarSize={22} />
              <Bar dataKey="enquiries" name="Enquiries" fill="#475569" radius={[2, 2, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 mb-4">All time</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={FileText} label="Quote requests" value={totals.quotes} testid="analytics-total-quotes" />
          <StatCard icon={Wrench} label="Service requests" value={totals.service_requests} testid="analytics-total-service-requests" />
          <StatCard icon={Briefcase} label="Job applications" value={totals.applications} testid="analytics-total-applications" />
        </div>
      </div>
    </div>
  );
};
